import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocationContext } from '../../context/LocationContext';
import { locationsApi } from '../../api/locationsApi';
import { syncApi } from '../../api/syncApi';
import { crmCatalogApi } from '../../api/crmCatalogApi';
import { Toast } from '../shared/Toast';
import { ConfirmDialog } from '../shared/ConfirmDialog';

const TERMINAL_STATES = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

export const Topbar: React.FC = () => {
    const {
        locations,
        currentLocation,
        locationId,
        setLocationId,
        loading,
        refreshLocations,
        setLocalSyncActive,
    } = useLocationContext();
    const [locMenuOpen, setLocMenuOpen] = useState(false);
    const [locQuery, setLocQuery] = useState('');
    const [locListMaxHeight, setLocListMaxHeight] = useState(320);
    const [syncMenuOpen, setSyncMenuOpen] = useState(false);
    const [confirmImport, setConfirmImport] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [togglingSync, setTogglingSync] = useState(false);
    const [mastersStatus, setMastersStatus] = useState<string | null>(null);
    const [lastSync, setLastSync] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const locTriggerRef = useRef<HTMLButtonElement>(null);
    const locPanelRef = useRef<HTMLDivElement>(null);
    const syncTriggerRef = useRef<HTMLDivElement>(null);
    const [locPanelStyle, setLocPanelStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
        const onOutsideClick = (e: MouseEvent) => {
            const target = e.target as Node;
            const insideLoc = locTriggerRef.current?.contains(target) || locPanelRef.current?.contains(target);
            if (!insideLoc) setLocMenuOpen(false);
            if (syncTriggerRef.current && !syncTriggerRef.current.contains(target)) setSyncMenuOpen(false);
        };
        document.addEventListener('mousedown', onOutsideClick);
        return () => document.removeEventListener('mousedown', onOutsideClick);
    }, []);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const openLocMenu = () => {
        if (locTriggerRef.current) {
            const rect = locTriggerRef.current.getBoundingClientRect();
            const top = rect.bottom + 8;
            setLocPanelStyle({ position: 'fixed', top, left: rect.left, width: 300, zIndex: 9999 });
            setLocListMaxHeight(Math.max(160, Math.min(320, window.innerHeight - top - 24)));
        }
        setLocMenuOpen(o => {
            const next = !o;
            if (next) setLocQuery('');
            return next;
        });
    };

    const filteredLocations = useMemo(() => {
        const q = locQuery.trim().toLowerCase();
        if (!q) return locations;
        return locations.filter(loc => loc.name.toLowerCase().includes(q) || loc.emr.toLowerCase().includes(q));
    }, [locations, locQuery]);

    const handleSyncEmrLocations = async () => {
        setSyncMenuOpen(false);
        setSyncing(true);
        setToast(null);
        try {
            const result = await syncApi.syncGhlEmrFromEngine();
            const parts = [];
            if (result.inserted > 0) parts.push(`${result.inserted} added`);
            if (result.updated > 0) parts.push(`${result.updated} updated`);
            if (result.skipped > 0) parts.push(`${result.skipped} skipped`);
            const summary = parts.length > 0 ? parts.join(', ') : 'already up to date';
            await refreshLocations();
            setToast({ message: `EMR sync complete — ${summary}`, type: 'success' });
            setLastSync('just now');
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Sync failed', type: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const isAvEmr = currentLocation?.crm === 'av_emr';

    const handleRefreshAvEmrCatalog = async () => {
        if (!currentLocation) return;
        setSyncMenuOpen(false);
        setSyncing(true);
        setToast(null);
        try {
            const r = await crmCatalogApi.refreshAvEmrCatalog(currentLocation.ghl_id);
            const parts = [`${r.providers_upserted} providers`, `${r.services_upserted} services`];
            const retired = r.providers_retired + r.services_retired;
            if (retired > 0) parts.push(`${retired} no longer in AV EMR`);
            setToast({ message: `AV EMR catalog refreshed — ${parts.join(', ')}`, type: 'success' });
            setLastSync('just now');
        } catch (err) {
            setToast({ message: err instanceof Error ? err.message : 'Failed to refresh the AV EMR catalog', type: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const handleToggleDataSync = async () => {
        if (!currentLocation || togglingSync) return;
        const next = !currentLocation.sync_active;
        const { ghl_id, emr } = currentLocation;
        setTogglingSync(true);
        setLocalSyncActive(ghl_id, emr, next);
        try {
            await locationsApi.setSyncActive(ghl_id, emr, next);
            setToast({
                message: next ? 'Data sync turned on' : 'Data sync turned off',
                type: 'success',
            });
        } catch (err) {
            setLocalSyncActive(ghl_id, emr, !next);
            setToast({
                message: err instanceof Error ? err.message : 'Failed to update data sync',
                type: 'error',
            });
        } finally {
            setTogglingSync(false);
        }
    };

    const handleSyncMasters = async () => {
        setSyncMenuOpen(false);
        setSyncing(true);
        setToast(null);
        setMastersStatus('Triggering…');
        try {
            const { execution } = await syncApi.triggerEmrMasters();
            setMastersStatus('Running…');
            let attempts = 0;
            pollRef.current = setInterval(async () => {
                attempts += 1;
                if (attempts > 20) {
                    clearInterval(pollRef.current!);
                    pollRef.current = null;
                    setMastersStatus(null);
                    setSyncing(false);
                    setToast({ message: "We couldn't confirm the import finished. It may still complete on its own — please refresh in a few minutes, or contact support if it keeps happening.", type: 'info' });
                    return;
                }
                try {
                    const { status } = await syncApi.getJobStatus(execution);
                    setMastersStatus(status.charAt(0) + status.slice(1).toLowerCase() + '…');
                    if (TERMINAL_STATES.includes(status)) {
                        clearInterval(pollRef.current!);
                        pollRef.current = null;
                        setMastersStatus(null);
                        setSyncing(false);
                        if (status === 'SUCCEEDED') {
                            setToast({ message: 'Sync Masters completed successfully', type: 'success' });
                            setLastSync('just now');
                        } else {
                            setToast({ message: `Sync Masters ${status.toLowerCase()}`, type: 'info' });
                        }
                    }
                } catch {
                    clearInterval(pollRef.current!);
                    pollRef.current = null;
                    setMastersStatus(null);
                    setSyncing(false);
                    setToast({ message: 'Could not fetch sync status', type: 'info' });
                }
            }, 3000);
        } catch (err) {
            setMastersStatus(null);
            setSyncing(false);
            setToast({ message: err instanceof Error ? err.message : 'Failed to trigger sync', type: 'info' });
        }
    };

    return (
        <header className="mapper-ui h-16 shrink-0 bg-mapper-white border-b border-mapper-border flex items-center justify-between px-7 relative z-30 font-sans text-mapper-text">
            {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

            <div className="flex items-center gap-3">
                <span className="text-[12.5px] font-semibold text-mapper-muted-2">Practice</span>
                <div className="relative">
                    <button
                        ref={locTriggerRef}
                        onClick={openLocMenu}
                        disabled={loading || locations.length === 0}
                        className="flex items-center gap-[10px] py-[7px] px-3 border border-mapper-border-2 rounded-[10px] bg-mapper-surface-2 disabled:opacity-60"
                    >
                        <div
                            className={`w-2 h-2 rounded-full ${currentLocation && !currentLocation.sync_active ? 'bg-mapper-red' : 'bg-mapper-green-dot'}`}
                            title={currentLocation && !currentLocation.sync_active ? 'Sync is inactive for this practice' : undefined}
                        />
                        <div className="text-left leading-[1.15]">
                            <div className="text-[13.5px] font-bold text-mapper-text">
                                {currentLocation?.name ?? (loading ? 'Loading…' : 'No practice')}
                            </div>
                        </div>
                        {currentLocation && (
                            <span className="inline-flex items-center gap-[5px] ml-[6px] px-2 py-[2px] rounded-[6px] bg-mapper-gray-bg text-[11px] font-semibold text-mapper-gray-text">
                                {currentLocation.emr}
                            </span>
                        )}
                        {isAvEmr && (
                            <span
                                title="This practice uses AV EMR as its CRM"
                                className="inline-flex items-center px-2 py-[2px] rounded-[6px] bg-mapper-purple-bg text-[11px] font-semibold text-mapper-purple"
                            >
                                AV EMR CRM
                            </span>
                        )}
                        <svg width="13" height="13" viewBox="0 0 12 12" fill="none" className="text-mapper-muted-3">
                            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    {locMenuOpen && createPortal(
                        <div ref={locPanelRef} style={locPanelStyle} className="mapper-ui font-sans bg-mapper-white border border-mapper-border-5 rounded-xl shadow-mapper-dropdown overflow-hidden">
                            <div className="p-[6px] pb-0">
                                <div className="relative">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#A6AEBA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-[10px] top-1/2 -translate-y-1/2">
                                        <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
                                    </svg>
                                    <input
                                        autoFocus
                                        value={locQuery}
                                        onChange={e => setLocQuery(e.target.value)}
                                        placeholder="Search practices…"
                                        className="w-full box-border pl-[30px] pr-3 py-[8px] mb-[6px] border border-mapper-border-2 rounded-[9px] text-[13px] text-mapper-body-text outline-none bg-white"
                                    />
                                </div>
                            </div>
                            <div className="overflow-y-auto p-[6px] pt-0" style={{ maxHeight: locListMaxHeight }}>
                                {filteredLocations.length === 0 ? (
                                    <div className="px-[11px] py-[16px] text-[13px] text-mapper-muted-2 text-center">No practices match "{locQuery}"</div>
                                ) : (
                                    filteredLocations.map(loc => (
                                        <button
                                            key={loc.ghl_id}
                                            onClick={() => { setLocationId(loc.ghl_id); setLocMenuOpen(false); }}
                                            className={`flex items-center gap-[11px] w-full px-[11px] py-[10px] rounded-[9px] text-left ${loc.ghl_id === locationId ? 'bg-mapper-teal-bg-2' : ''}`}
                                        >
                                            <div className={`w-2 h-2 rounded-full shrink-0 ${loc.ghl_id === locationId ? 'bg-mapper-green-dot' : 'bg-mapper-muted-6'}`} />
                                            <div className="flex-1 leading-[1.2]">
                                                <div className="text-[13.5px] font-semibold text-mapper-text">{loc.name}</div>
                                            </div>
                                            <span className="px-2 py-[2px] rounded-[6px] bg-mapper-gray-bg text-[10.5px] font-semibold text-mapper-gray-text">{loc.emr}</span>
                                        </button>
                                    ))
                                )}
                            </div>
                        </div>,
                        document.body
                    )}
                </div>
                {currentLocation && (
                    <div className="flex items-center gap-2 ml-1">
                        <span className="text-[12.5px] font-semibold text-mapper-muted-2">Data Sync</span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={currentLocation.sync_active}
                            aria-label={currentLocation.sync_active ? 'Turn data sync off' : 'Turn data sync on'}
                            disabled={togglingSync}
                            onClick={handleToggleDataSync}
                            className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
                                currentLocation.sync_active ? 'bg-mapper-teal' : 'bg-mapper-muted-6'
                            }`}
                        >
                            <span
                                className={`pointer-events-none absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                                    currentLocation.sync_active ? 'translate-x-4' : 'translate-x-0'
                                }`}
                            />
                        </button>
                    </div>
                )}
            </div>

            <div className="flex items-center gap-3">
                <div className="flex items-center gap-[7px] text-[12.5px] text-mapper-muted-2">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
                    </svg>
                    Last sync <span className="text-mapper-body-text-2 font-semibold">{lastSync ?? '—'}</span>
                </div>
                <div className="relative" ref={syncTriggerRef}>
                    <button
                        onClick={() => !syncing && setSyncMenuOpen(o => !o)}
                        disabled={syncing}
                        className="flex items-center gap-2 py-[9px] pl-4 pr-3 rounded-[10px] bg-mapper-teal text-white text-[13.5px] font-semibold shadow-mapper-btn disabled:opacity-75"
                    >
                        <span className={`inline-flex ${syncing ? 'animate-spin' : ''}`}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1-7.9-4.7" /><path d="M3 12a9 9 0 0 1 9-9 9 9 0 0 1 7.9 4.7" />
                                <path d="M21 4v4h-4" /><path d="M3 20v-4h4" />
                            </svg>
                        </span>
                        {mastersStatus ?? (syncing ? 'Syncing…' : 'Sync')}
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="ml-1">
                            <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    {syncMenuOpen && (
                        <div className="absolute right-0 top-[calc(100%+8px)] w-[280px] bg-mapper-white border border-mapper-border-5 rounded-xl shadow-mapper-dropdown p-[6px] z-40">
                            <button onClick={handleSyncEmrLocations} className="block w-full px-3 py-[9px] rounded-[8px] text-left hover:bg-mapper-bg">
                                <div className="text-[13px] font-semibold text-mapper-text">Refresh locations</div>
                                <div className="text-[11.5px] text-mapper-muted-2 leading-[1.35] mt-[1px]">Pull your latest Advital locations and their EMR links.</div>
                            </button>
                            <button onClick={() => { setSyncMenuOpen(false); setConfirmImport(true); }} className="block w-full px-3 py-[9px] rounded-[8px] text-left hover:bg-mapper-bg">
                                <div className="text-[13px] font-semibold text-mapper-text">Import latest data</div>
                                <div className="text-[11.5px] text-mapper-muted-2 leading-[1.35] mt-[1px]">Re-import appointment types, providers and statuses from your scheduling system. Takes a few minutes.</div>
                            </button>
                            {isAvEmr && (
                                <button onClick={handleRefreshAvEmrCatalog} className="block w-full px-3 py-[9px] rounded-[8px] text-left hover:bg-mapper-bg">
                                    <div className="text-[13px] font-semibold text-mapper-text">Refresh AV EMR catalog</div>
                                    <div className="text-[11.5px] text-mapper-muted-2 leading-[1.35] mt-[1px]">Pull this practice's AV EMR providers and services to map against.</div>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <ConfirmDialog
                open={confirmImport}
                title="Import latest data?"
                message="This re-imports your appointment types, providers and statuses from your scheduling system. It can take a few minutes, and you may briefly see fewer items while it runs. You can keep working in the meantime."
                confirmLabel="Import data"
                onConfirm={() => { setConfirmImport(false); handleSyncMasters(); }}
                onCancel={() => setConfirmImport(false)}
            />
        </header>
    );
};
